/**
 * POPSTAR settings — account + preferences for a named user: display name,
 * email, password, and UI language. Presentational + human-timescale only; it
 * reads the current account via the {@link GameServices} surface and performs
 * only DISCRETE actions on it.
 *
 * Layout mirrors the legacy POPSTAR settings screen (AVOID_LEGACY_ANALYSIS §2.13:
 * email / display-name / password change / language), restyled in the glamorous
 * glass palette the `.settings-screen` family re-maps (deep purple → magenta with
 * a gold accent). All colour/blur/radius/shadow come from the shared design
 * tokens in `src/index.css` — nothing is inlined here.
 *
 * CONTRACT NOTE: the locked {@link GameServices} interface currently exposes only
 * `resetPassword` and `logOut` as account mutators (name/email/language setters
 * are not yet on the contract — they arrive in a later network phase). So this
 * screen shows the current name/email/language read-only, wires the password
 * change through the email reset flow, lets the user pick a language locally, and
 * offers log out. When the contract gains profile setters, the inputs here become
 * editable with a save handler — the layout is already in place.
 */
import { useCallback, useEffect, useState } from 'react';
import type { GameServices } from '../services/types';
import { RequiresAccountError } from '../services/types';
import type { UserProfile } from '../services/dataModel';
import { LANGS, useI18n } from './i18n';
import type { Lang } from './i18n';

interface SettingsScreenProps {
  services: GameServices;
  /** Back to the network shell hub. */
  onBack: () => void;
  /** Called after a successful log out (parent returns the user to anon play). */
  onLoggedOut: () => void;
}

export function SettingsScreen({ services, onBack, onLoggedOut }: SettingsScreenProps) {
  // The live UI language comes from the i18n context (persisted to localStorage and,
  // best-effort, to the account via App's onChange → services.setLanguage). Changing
  // it here re-renders the whole app in the new language immediately.
  const { lang, setLang, t } = useI18n();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // One-shot read of the current profile to seed the displayed fields.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const p = await services.getProfile();
        if (cancelled) return;
        setProfile(p);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof RequiresAccountError
            ? 'Log in to manage your account.'
            : err instanceof Error
              ? err.message
              : 'Could not load your settings.',
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [services]);

  // The signed-in account, read from the auth surface. Email lives on
  // `currentUser().email` (NOT `.name` — that's the display name); the profile
  // read above seeds the display name, falling back to the auth user.
  const account = services.currentUser();
  const email = account.email ?? '';
  const name = profile?.name ?? account.name ?? '';

  const sendPasswordReset = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      // The contract's password change is email-based: trigger a reset link to
      // the account's email. We resolve the email from the auth user.
      const target = services.currentUser().email ?? '';
      if (!target) {
        setError('No email is associated with this account.');
        return;
      }
      await services.resetPassword(target);
      setNotice('A password reset link is on its way to your email.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the reset email.');
    } finally {
      setBusy(false);
    }
  }, [busy, services]);

  const logOut = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await services.logOut();
      onLoggedOut();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not log out.');
      setBusy(false);
    }
  }, [busy, services, onLoggedOut]);

  return (
    <div className="screen network-screen settings-screen">
      <header className="network-head">
        <button className="btn btn-ghost network-back" onClick={onBack} type="button">
          ‹ {t('common.back')}
        </button>
        <h1 className="network-title">{t('settings.title')}</h1>
      </header>

      <section className="panel">
        <h2>{t('settings.account')}</h2>
        <label className="field">
          <span className="field-label">{t('settings.displayName')}</span>
          <input
            className="field-input"
            type="text"
            value={name}
            readOnly
            aria-readonly
            placeholder="Your display name"
            autoComplete="name"
          />
        </label>
        <label className="field">
          <span className="field-label">{t('settings.email')}</span>
          <input
            className="field-input"
            type="email"
            value={email}
            readOnly
            aria-readonly
            placeholder="Your account email"
            autoComplete="email"
          />
        </label>
        <p className="muted-line settings-hint">
          Profile editing arrives with the full network phase.
        </p>
      </section>

      <section className="panel">
        <h2>{t('settings.password')}</h2>
        <p className="muted-line">
          We send a secure reset link to your email rather than handling your
          password directly.
        </p>
        <button
          className="btn btn-ghost settings-action"
          type="button"
          onClick={() => void sendPasswordReset()}
          disabled={busy}
        >
          {t('settings.sendReset')}
        </button>
      </section>

      <section className="panel">
        <h2>{t('settings.language')}</h2>
        <label className="field">
          <span className="field-label">{t('settings.uiLanguage')}</span>
          <select
            className="field-input"
            value={lang}
            onChange={(e) => setLang(e.target.value as Lang)}
          >
            {LANGS.map((l) => (
              <option key={l.code} value={l.code}>
                {l.label}
              </option>
            ))}
          </select>
        </label>
        <p className="muted-line settings-hint">{t('settings.langHint')}</p>
      </section>

      {error && (
        <p className="auth-error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="auth-notice" role="status">
          {notice}
        </p>
      )}

      <button
        className="btn btn-danger settings-logout"
        type="button"
        onClick={() => void logOut()}
        disabled={busy}
      >
        {t('settings.logout')}
      </button>

      <style>{`
        /* Scoped, settings-only refinements. Colours/blur/radius/shadow and the
           shared bits (.settings-screen .panel width, .settings-logout width,
           .settings-hint, the glamorous palette re-map) live in src/index.css;
           this block only adds the layout the global sheet does not own. */
        .settings-screen {
          gap: var(--space-4);
        }
        /* Password CTA is full-width so it reads as the panel's primary action. */
        .settings-action {
          width: 100%;
          margin-top: var(--space-3);
        }
      `}</style>
    </div>
  );
}
